import React, { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import SplashPage from "@/pages/SplashPage";
import AccessCodePage from "@/pages/AccessCodePage";
import NDAPage from "@/pages/NDAPage";
import RegisterPage from "@/pages/RegisterPage";
import LoginPage from "@/pages/LoginPage";
import HomePage from "@/pages/HomePage";
import CRMPage from "@/pages/CRMPage";
import DirectLoginPage from "@/pages/DirectLoginPage";
import RulesPage from "@/pages/RulesPage";
import SponsorsPage from "@/pages/SponsorsPage";
import AppsPage from "@/pages/AppsPage";
import ContactPage from "@/pages/ContactPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import MissionPage from "@/pages/MissionPage";
import AboutPage from "@/pages/AboutPage";
import AdminPage from "@/pages/AdminPage";
import ProfilePage from "@/pages/ProfilePage";
import VenuePartnershipPage from "@/pages/VenuePartnershipPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import { TournamentPage, TournamentListPage } from "@/pages/TournamentPage";
import EventDetailPage from "@/pages/EventDetailPage";
import DiscoverPlayersPage from "@/pages/DiscoverPlayersPage";
import WalkthroughPage from "@/pages/WalkthroughPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelledPage from "@/pages/PaymentCancelledPage";

// Import centralized API configuration
import { API } from './config/api';
export { API };

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("b2b_token"));
  const [loading, setLoading] = useState(true);
  const [accessGranted, setAccessGranted] = useState(
    localStorage.getItem("b2b_access") === "true"
  );
  const [ndaSigned, setNdaSigned] = useState(
    localStorage.getItem("b2b_nda") === "true"
  );

  useEffect(() => {
    const fetchUser = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setUser(response.data);
        } catch (error) {
          console.error("Auth error:", error);
          logout();
        }
      }
      setLoading(false);
    };
    fetchUser();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("b2b_token", newToken);
    setToken(newToken);
    setUser(userData);
    return response.data;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("b2b_token", newToken);
    setToken(newToken);
    setUser(userData);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("b2b_token");
    setToken(null);
    setUser(null);
  };

  const grantAccess = () => {
    localStorage.setItem("b2b_access", "true");
    setAccessGranted(true);
  };

  const signNda = () => {
    localStorage.setItem("b2b_nda", "true");
    setNdaSigned(true);
  };

  const adminLogin = async (password, type = "direct") => {
    const endpoint = type === "crm" ? "/admin/crm-login" : "/admin/direct-login";
    const response = await axios.post(`${API}${endpoint}`, { password });
    const { token: newToken } = response.data;
    localStorage.setItem("b2b_token", newToken);
    // Admin bypasses access code and NDA
    localStorage.setItem("b2b_access", "true");
    localStorage.setItem("b2b_nda", "true");
    setToken(newToken);
    setUser({ role: type === "crm" ? "crm_admin" : "admin" });
    setAccessGranted(true);
    setNdaSigned(true);
    return response.data;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        register,
        logout,
        accessGranted,
        ndaSigned,
        grantAccess,
        signNda,
        adminLogin,
        isAuthenticated: !!token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requireAuth = true, requireAccess = true, requireNda = true }) => {
  const { isAuthenticated, accessGranted, ndaSigned, loading } = useAuth();
  const location = useLocation();

  // Also check localStorage directly for immediate auth check
  const hasToken = !!localStorage.getItem("b2b_token");
  const hasAccess = localStorage.getItem("b2b_access") === "true";
  const hasNda = localStorage.getItem("b2b_nda") === "true";

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-pulse-gold w-16 h-16 rounded-full border-2 border-[#7a6200]"></div>
      </div>
    );
  }

  // Use either state or localStorage values
  const effectiveAuth = isAuthenticated || hasToken;
  const effectiveAccess = accessGranted || hasAccess;
  const effectiveNda = ndaSigned || hasNda;

  if (requireAccess && !effectiveAccess) {
    return <Navigate to="/access" state={{ from: location }} replace />;
  }

  if (requireNda && !effectiveNda) {
    return <Navigate to="/nda" state={{ from: location }} replace />;
  }

  if (requireAuth && !effectiveAuth) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-[#050505]">
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<SplashPage />} />
            <Route path="/access" element={<AccessCodePage />} />
            <Route path="/nda" element={<NDAPage />} />
            <Route path="/register" element={
              <ProtectedRoute requireAuth={false} requireNda={true}>
                <RegisterPage />
              </ProtectedRoute>
            } />
            <Route path="/login" element={
              <ProtectedRoute requireAuth={false} requireNda={false} requireAccess={false}>
                <LoginPage />
              </ProtectedRoute>
            } />
            
            {/* Admin routes */}
            <Route path="/direct" element={<DirectLoginPage />} />
            <Route path="/direct_login" element={<DirectLoginPage />} />
            <Route path="/crm" element={<CRMPage />} />
            <Route path="/walkthrough" element={<WalkthroughPage />} />
            
            {/* Public info pages */}
            <Route path="/rules" element={<RulesPage />} />
            <Route path="/sponsors" element={<SponsorsPage />} />
            <Route path="/apps" element={<AppsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/mission" element={<MissionPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/admin" element={<AdminPage />} />
            
            {/* Protected routes */}
            <Route path="/home" element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />
            <Route path="/venue-partnership" element={
              <ProtectedRoute>
                <VenuePartnershipPage />
              </ProtectedRoute>
            } />
            <Route path="/leaderboard" element={
              <ProtectedRoute>
                <LeaderboardPage />
              </ProtectedRoute>
            } />
            <Route path="/tournaments" element={
              <ProtectedRoute>
                <TournamentListPage />
              </ProtectedRoute>
            } />
            <Route path="/tournaments/:tournamentId" element={
              <ProtectedRoute>
                <TournamentPage />
              </ProtectedRoute>
            } />

            {/* Event Detail Page */}
            <Route path="/events/:eventId" element={
              <ProtectedRoute>
                <EventDetailPage />
              </ProtectedRoute>
            } />

            {/* Discover Players Page */}
            <Route path="/discover" element={
              <ProtectedRoute>
                <DiscoverPlayersPage />
              </ProtectedRoute>
            } />

            {/* Payment Pages */}
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/payment-cancelled" element={<PaymentCancelledPage />} />
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
